import React, { useEffect } from "react";
import {
    FaPoundSign,
    FaCheckCircle,
    FaTimesCircle,
    FaShieldAlt,
    FaStar,
    FaAward,
    FaGem,
    FaCrown,
    FaCalendarAlt
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import "../styles/Subscription.css";
import { toast } from "react-toastify";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

export default function CreateSubscription() {
    const location = useLocation();
    const navigate = useNavigate();
    const { login } = useAuth();
    const signupData = location.state?.signupData;
    const hideLayout = location.pathname === "/solicitor/dashboard";
    const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

    /* ===============================
       🔒 SECURITY CHECK
    =============================== */
    useEffect(() => {
        if (!signupData || signupData.role !== "SOLICITOR") {
            toast.error("Invalid access to subscription page");
            navigate("/signup", { replace: true });
        }
    }, [signupData, navigate]);

    /* ===============================
       ✅ HANDLE SUCCESS
    =============================== */
    const handleSuccessfulSignup = async (token) => {
        try {
            const meRes = await fetch(`${API_BASE_URL}/api/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const meData = await meRes.json();

            if (!meRes.ok) {
                toast.error("Failed to load user data");
                return;
            }

            // Update AuthContext
            login(token, meData.user);

            // ✅ Show Success Alert
            toast.success("Payment Successful! Redirecting to dashboard...");

            // ⏳ Wait 3 seconds then redirect
            setTimeout(() => {
                navigate("/solicitor/dashboard", { replace: true });
            }, 3000);

        } catch (err) {
            console.error(err);
            toast.error("Something went wrong after signup");
        }
    };

    /* ===============================
       💳 COMPLETE SIGNUP CALL
    =============================== */
    const completeSignup = async ({
        paymentId,
        planName,
        planType,
        amount
    }) => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/complete-signup`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    signupData,
                    paymentId,
                    planName,
                    planType,
                    amount,
                    paymentStatus: "SUCCESS"
                })
            });

            const result = await res.json();

            if (!res.ok) {
                toast.error(result.message);
                return;
            }

            await handleSuccessfulSignup(result.token);

        } catch (err) {
            console.error(err);
            toast.error("Signup failed");
        }
    };

    if (!signupData) return null;

    return (
        <>
            {!hideLayout && <Header />}
            <div className="subscription-container">
                <h1 className="subscription-title">
                    Choose Your Justivon Plan
                </h1>

                <p className="subscription-subtitle">
                    Boost visibility. Increase client bookings. Dominate AI rankings.
                </p>

                {/* ================= PLANS ================= */}

                <PayPalScriptProvider
                    options={{
                        "client-id": paypalClientId,
                        currency: "GBP"
                    }}
                >
                    <div className="plans-wrapper">

                        {/* ================= FREE TRIAL ================= */}
                        <div className="plan-card">
                            <h3>Free Trial</h3>
                            <div className="price">
                                <FaPoundSign />0
                            </div>
                            <p className="duration">7 Days Access</p>

                            <ul className="features-list">
                                <li><FaCheckCircle className="check-icon" /> Profile Listing</li>
                                <li><FaCheckCircle className="check-icon" /> Standard AI Visibility</li>
                                <li><FaCheckCircle className="check-icon" /> 3 Booking Requests</li>
                                <li><FaCheckCircle className="check-icon" /> Basic Analytics</li>
                                <li className="restriction">
                                    <FaTimesCircle className="times-icon" /> No AI Boost
                                </li>
                                <li className="restriction"><FaTimesCircle /> No Export Data</li>
                            </ul>

                            <button
                                className="plan-btn secondary"
                                onClick={() =>
                                    completeSignup({
                                        paymentId: "FREE_TRIAL",
                                        planName: "FREE",
                                        planType: "TRIAL",
                                        amount: 0
                                    })
                                }
                            >
                                Start Free Trial
                            </button>
                        </div>

                        {/* ================= PROFESSIONAL ================= */}
                        <div className="plan-card professional">
                            <div className="badge popular">
                                <FaStar /> MOST POPULAR
                            </div>

                            <h3>Professional</h3>
                            <div className="price">
                                <FaPoundSign />8.17
                            </div>
                            <p className="duration">Per Month</p>

                            <ul className="features-list">
                                <li><FaCheckCircle className="check-icon" /> Priority Search Placement</li>
                                <li><FaCheckCircle className="check-icon" /> AI Boost (1.2x)</li>
                                <li><FaCheckCircle className="check-icon" /> <FaAward /> Recommended Badge</li>
                                <li><FaCheckCircle className="check-icon" /> Booking Analytics</li>
                                <li><FaCheckCircle className="check-icon" /> Export Data</li>
                                <li><FaCheckCircle className="check-icon" /> 30 Bookings / Month</li>
                            </ul>

                            <div style={{ marginTop: "15px" }}>
                                <PayPalButtons
                                    style={{ layout: "vertical" }}
                                    createOrder={(data, actions) =>
                                        actions.order.create({
                                            purchase_units: [
                                                {
                                                    amount: {
                                                        value: "8.17",
                                                        currency_code: "GBP"
                                                    }
                                                }
                                            ]
                                        })
                                    }
                                    onApprove={async (data, actions) => {
                                        const details = await actions.order.capture();

                                        completeSignup({
                                            paymentId: details.id,
                                            planName: "PROFESSIONAL",
                                            planType: "MONTHLY",
                                            amount: 8.17
                                        });
                                    }}
                                    onCancel={() => toast.error("Payment Cancelled")}
                                    onError={() => toast.error("Payment Failed")}
                                />
                            </div>
                        </div>

                        {/* ================= ENTERPRISE ================= */}
                        <div className="plan-card enterprise">
                            <div className="badge recommend">
                                <FaGem /> RECOMMENDED
                            </div>

                            <h3>Enterprise Annual</h3>
                            <div className="price">
                                <FaPoundSign />89.83
                            </div>

                            <p className="duration highlight">
                                <FaCalendarAlt /> Pay for 11 months, get 1 month FREE
                            </p>

                            <ul className="features-list">
                                <li><FaCheckCircle className="check-icon" /> Top Search Placement</li>
                                <li><FaCheckCircle className="check-icon" /> Max AI Boost (1.5x)</li>
                                <li><FaCheckCircle className="check-icon" /> <FaCrown /> Gold Badge</li>
                                <li><FaCheckCircle className="check-icon" /> Unlimited Bookings</li>
                                <li><FaCheckCircle className="check-icon" /> Advanced Analytics</li>
                                <li><FaCheckCircle className="check-icon" /> Priority Support</li>
                                <li><FaCheckCircle className="check-icon" /> Export Data</li>
                            </ul>

                            <div style={{ marginTop: "15px" }}>
                                <PayPalButtons
                                    style={{ layout: "vertical" }}
                                    createOrder={(data, actions) =>
                                        actions.order.create({
                                            purchase_units: [
                                                {
                                                    amount: {
                                                        value: "89.83",
                                                        currency_code: "GBP"
                                                    }
                                                }
                                            ]
                                        })
                                    }
                                    onApprove={async (data, actions) => {
                                        const details = await actions.order.capture();

                                        completeSignup({
                                            paymentId: details.id,
                                            planName: "ENTERPRISE",
                                            planType: "ANNUAL",
                                            amount: 89.83
                                        });
                                    }}
                                    onCancel={() => toast.error("Payment Cancelled ❌")}
                                    onError={() => toast.error("Payment Failed ❌")}
                                />
                            </div>
                        </div>

                    </div>
                </PayPalScriptProvider>

                {/* ================= PLAN COMPARISON ================= */}

                <div className="comparison-section">
                    <h2>Plan Comparison</h2>

                    <div className="table-responsive">
                        <table className="comparison-table">
                            <thead>
                                <tr>
                                    <th>Features</th>
                                    <th>Free</th>
                                    <th>Professional</th>
                                    <th>Enterprise Annual</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="feature-name">Profile Listings</td>
                                    <td className="feature-value">1</td>
                                    <td className="feature-value">30 / month</td>
                                    <td className="feature-value">Unlimited</td>
                                </tr>
                                <tr>
                                    <td className="feature-name">AI Ranking Boost</td>
                                    <td className="feature-value"><FaTimesCircle className="times-icon" /></td>
                                    <td className="feature-value">1.2x</td>
                                    <td className="feature-value">1.5x</td>
                                </tr>
                                <tr>
                                    <td className="feature-name">Badge</td>
                                    <td className="feature-value"><FaTimesCircle className="times-icon" /></td>
                                    <td className="feature-value">
                                        <div className="badge-cell">
                                            <FaAward className="badge-icon" /> Recommended
                                        </div>
                                    </td>
                                    <td className="feature-value">
                                        <div className="badge-cell">
                                            <FaCrown className="crown-icon" /> Gold
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="feature-name">Chat Option</td>
                                    <td className="feature-value"><FaTimesCircle className="times-icon" /></td>
                                    <td className="feature-value"><FaCheckCircle className="check-icon" /></td>
                                    <td className="feature-value"><FaCheckCircle className="check-icon" /></td>
                                </tr>
                                <tr>
                                    <td className="feature-name">Analytics</td>
                                    <td className="feature-value">Basic</td>
                                    <td className="feature-value">Advanced</td>
                                    <td className="feature-value">Advanced + Insights</td>
                                </tr>
                                <tr>
                                    <td className="feature-name">Priority Support</td>
                                    <td className="feature-value"><FaTimesCircle className="times-icon" /></td>
                                    <td className="feature-value"><FaTimesCircle className="times-icon" /></td>
                                    <td className="feature-value"><FaCheckCircle className="check-icon" /></td>
                                </tr>
                                <tr>
                                    <td className="feature-name">Dedicated Account Manager</td>
                                    <td className="feature-value"><FaTimesCircle className="times-icon" /></td>
                                    <td className="feature-value"><FaTimesCircle className="times-icon" /></td>
                                    <td className="feature-value"><FaCheckCircle className="check-icon" /></td>
                                </tr>
                                <tr>
                                    <td className="feature-name">Annual Discount</td>
                                    <td className="feature-value"><FaTimesCircle className="times-icon" /></td>
                                    <td className="feature-value"><FaTimesCircle className="times-icon" /></td>
                                    <td className="feature-value highlight-cell">1 Month Free</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ================= PAYMENT SECURITY ================= */}

                <div className="payment-security">
                    <FaShieldAlt /> All payments are securely processed with industry-standard encryption.
                </div>

                {/* ================= TESTIMONIALS ================= */}

                <div className="testimonial-section">
                    <h2>What Solicitors Say About Justivon</h2>

                    <div className="testimonial-wrapper">

                        <div className="testimonial-card">
                            <p>
                                "The Professional plan increased my case inquiries within
                                the first month."
                            </p>
                            <h4>James Carter</h4>
                            <span>Criminal Defense Solicitor</span>
                        </div>

                        <div className="testimonial-card">
                            <p>
                                "Enterprise gave our firm premium homepage visibility
                                and consistent high-value clients."
                            </p>
                            <h4>Olivia Bennett</h4>
                            <span>Family Law Specialist</span>
                        </div>

                        <div className="testimonial-card">
                            <p>
                                "Started with Free trial and upgraded quickly.
                                Analytics helped track conversions."
                            </p>
                            <h4>Daniel Wright</h4>
                            <span>Immigration Solicitor</span>
                        </div>

                    </div>
                </div>

            </div>
            {!hideLayout && <Footer />}
        </>
    );
}